import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import Header from "components/Header";
import { DataGrid } from '@mui/x-data-grid';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const WorkProcess = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [currentProcess, setCurrentProcess] = useState(null);
  const [title, setTitle] = useState("");
  const [processList, setProcessList] = useState([{ title: "", description: "", image: "" }]);
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchProcesses();
  }, []);

  const fetchProcesses = async () => {
    try {
      const response = await axios.get(`${apiUrl}/workprocess/getworkprocess`);
      setData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleFileChangeProcess = (index, event) => {
    const selectedImage = event.target.files[0];
    if (selectedImage) {
      handleImageUpload(selectedImage, (filePath) => {
        const updatedProcesses = [...processList];
        updatedProcesses[index].image = filePath; // Store the image path for the process
        setProcessList(updatedProcesses);
      });
    }
  };

  const handleImageUpload = async (file, callback) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(`${apiUrl}/upload`, formData);
      // callback(response.data.filePath); // Use callback to set the image path
      callback(response.data.url);
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage("Error uploading image.");
      setSnackbarOpen(true);
    }
  };

  const handleOpenModal = (process = null) => {
    if (process) {
      setCurrentProcess(process);
      setTitle(process.title);
      setProcessList(process.process || []); // Default to an empty array if process is undefined
    } else {
      setCurrentProcess(null);
      setTitle("");
      setProcessList([{ title: "", description: "", image: "" }]); // Start with one empty process
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentProcess(null);
  };

  const handleProcessChange = (index, e) => {
    const updatedProcesses = processList.map((item, i) =>
      i === index ? { ...item, [e.target.name]: e.target.value } : item
    );
    setProcessList(updatedProcesses);
  };

  const handleAddProcess = () => {
    setProcessList([...processList, { title: "", description: "", image: "" }]);
  };

  const handleRemoveProcess = (index) => {
    setProcessList(processList.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!title) return false;
    for (let proces of processList) {
      if (!proces.title || !proces.description || !proces.image) return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      setErrorMessage("Please fill all the required fields.");
      setSnackbarOpen(true);
      return;
    }
    const postData = {
      title,
      process: processList.map(proces => ({
        ...proces,
        image: proces.image, // Using the image path from the process
      })),
    };

    try {
      const response = await axios.post(`${apiUrl}/workprocess/createworkprocess`, postData);
      setData((prevData) => [...prevData, response.data]);
      handleCloseModal();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      setErrorMessage("Please fill all the required fields.");
      setSnackbarOpen(true);
      return;
    }
    const updateData = {
      title,
      process: processList.map(proces => ({
        ...proces,
        image: proces.image, // Using the image path from the process
      })),
    };

    try {
      const response = await axios.put(`${apiUrl}/workprocess/updateworkprocess/${currentProcess._id}`, updateData);
      setData((prevData) =>
        prevData.map((item) => (item._id === currentProcess._id ? response.data : item))
      );
      handleCloseModal();
    } catch (error) {
      console.error("Error updating process:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/workprocess/deleteworkprocess/${id}`);
      setData((prevData) => prevData.filter((item) => item._id !== id));
    } catch (error) {
      console.error("Error deleting process:", error);
    }
  };

  const columns = [
     
    { field: "title", headerName: "Title", flex: 1 },
    {
      field: "process",
      headerName: "Processes",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? params.value.map((p) => p.title).join(", ") : "No Processes"}
        </Typography>
      ),
    },
    {
      field: "images",
      headerName: "Images",
      flex: 1,
      renderCell: (params) => (
        <img src={params.row.process[0].image} alt={params.row.process.title} width="50" height="50" />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <>
          <Button variant="contained"  style={{marginRight: 8}} color="primary" size="small" onClick={() => handleOpenModal(params.row)}>
            Update
          </Button>
          <Button variant="contained" color="secondary" size="small" onClick={() => handleDelete(params.row._id)}>
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="Work Process" subtitle="Work Processes" />
      <Button variant="contained" color="primary" onClick={() => handleOpenModal(null)}>
        Add New Work Process
      </Button>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>{currentProcess ? "Update Work Process" : "Add New Work Process"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Typography variant="subtitle1">Processes</Typography>
          {processList.map((p, index) => (
            <Box key={index} display="flex" alignItems="center" mb={2}>
              <TextField
                margin="dense"
                label="Process Title"
                name="title"
                type="text"
                variant="outlined"
                value={p.title}
                onChange={(e) => handleProcessChange(index, e)}
                style={{ flex: 1, marginRight: "8px" }}
              />
              <TextField
                margin="dense"
                label="Process Description"
                name="description"
                type="text"
                variant="outlined"
                value={p.description}
                onChange={(e) => handleProcessChange(index, e)}
                style={{ flex: 1, marginRight: "8px" }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  onChange={(e) => handleFileChangeProcess(index, e)}  
                />
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => handleRemoveProcess(index)}>
                Remove
              </Button>
              {p.image && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <img src={p.image} alt="image" width="50" height="50" />
                </Typography>
              )}
            </Box>
          ))}
          <Button variant="contained" color="primary" onClick={handleAddProcess}>
            Add Process
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button onClick={currentProcess ? handleUpdate : handleSubmit} color="primary">
            {currentProcess ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt="40px" height="75vh">
        <DataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={data || []}
          columns={columns}
          pageSize={5}
        />
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkProcess;
